import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { withStyles } from '@mui/styles';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment';
import { Styles } from '../../styles/material-styles';
import CustomizedSnackbars from '../../components/material-snackbars';
import HeaderMenu from '../../components/header-menu';
import { findCases, hasAccess, revert } from '../../services/govunificationrevert.service';

const errorMessage = (e, fallback) => {
  const errors = e?.response?.data?.errors;
  const first = Array.isArray(errors) && errors.length > 0 ? errors[0] : null;
  return (first && (first.message || first)) || fallback;
};

const Field = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography sx={{ fontSize: 14, wordBreak: 'break-all' }}>{value || '—'}</Typography>
  </Box>
);

const UserBlock = ({ title, user, chips }) => (
  <Box sx={{ flex: 1, minWidth: 260 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
      {chips}
    </Box>
    {user ? (
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <Field label="ID" value={user.id} />
        <Field label="Nome" value={user.name} />
        <Field label="E-mail" value={user.email} />
        <Field label="Documento" value={user.document} />
      </Box>
    ) : (
      <Typography color="text.secondary" sx={{ fontSize: 14 }}>Não encontrado.</Typography>
    )}
  </Box>
);

function GovUnificationRevert({ classes, headerMenu, actionsHeaderMenu }) {
  const [allowed, setAllowed] = useState(null); // null = verificando

  useEffect(() => {
    let active = true;
    hasAccess().then((ok) => { if (active) setAllowed(ok); });
    return () => { active = false; };
  }, []);

  const [email, setEmail] = useState('');
  const [searchedEmail, setSearchedEmail] = useState('');
  const [cases, setCases] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmCase, setConfirmCase] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [done, setDone] = useState([]);
  const [snack, setSnack] = useState({ open: false, variant: 'error', message: '' });

  const notify = (variant, message) => setSnack({ open: true, variant, message });

  const search = async (targetEmail) => {
    const value = (targetEmail || email).trim();
    if (!value) return;
    setLoading(true);
    try {
      const res = await findCases(value);
      setSearchedEmail(value);
      setCases(res?.data?.data || []);
    } catch (e) {
      setCases(null);
      notify('error', errorMessage(e, 'Não foi possível consultar a unificação.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setDone([]);
    search();
  };

  const handleRevert = async () => {
    const target = confirmCase;
    if (!target) return;
    setReverting(true);
    try {
      const res = await revert(target.deleteduser.id, target.principaluserid);
      setDone((prev) => [...prev, res?.data?.data]);
      setConfirmCase(null);
      notify('success', 'Unificação revertida. O usuário já pode acessar com o e-mail restaurado.');
      await search(searchedEmail);
    } catch (e) {
      setConfirmCase(null);
      notify('error', errorMessage(e, 'Não foi possível reverter a unificação.'));
      await search(searchedEmail);
    } finally {
      setReverting(false);
    }
  };

  return (
    <div className={classes.root}>
      <HeaderMenu />
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: headerMenu.open,
        }, classes.actionsContent, {
          [classes.actionscontentShift]: actionsHeaderMenu.open,
        })}
        style={{ paddingTop: 'calc(6px + var(--mexx-breadcrumb-height, 0px))' }}
      >
        <div className={classes.drawerHeader} />
        <Box sx={{ px: 2, pt: 2, pb: 4, maxWidth: 1100, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
              }}
            >
              <SwapHorizIcon sx={{ color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 700 }}>Reverter unificação de login (gov.br)</Typography>
              <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                Devolve o e-mail do usuário removido na unificação ao usuário principal e força um novo fluxo de autenticação.
              </Typography>
            </Box>
          </Box>

          {allowed === null ? (
            <CircularProgress size={24} />
          ) : !allowed ? (
            <Alert severity="warning">Você não tem acesso a esta funcionalidade.</Alert>
          ) : (
            <>
              <Card variant="outlined">
                <CardContent>
                  <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      label="E-mail do usuário removido"
                      placeholder="nome.sobrenome@orgao.gov.br"
                      size="small"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      sx={{ flex: 1, minWidth: 280 }}
                      autoFocus
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                      disabled={loading || !email.trim()}
                    >
                      Consultar
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {done.map((result, idx) => (
                <Alert key={idx} severity="success">
                  Usuário principal {result?.principaluserid} agora usa o e-mail {result?.newemail} (antes: {result?.oldemail}).
                  {' '}Registros de opt-in invalidados: {result?.optinsdeleted}.
                </Alert>
              ))}

              {cases && cases.length === 0 && (
                <Alert severity="info">
                  Nenhum usuário removido por unificação foi encontrado para <strong>{searchedEmail}</strong>. Confira o e-mail
                  ou se a reversão já foi realizada.
                </Alert>
              )}

              {(cases || []).map((c) => (
                <Card key={c.deleteduser.id} variant="outlined">
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <UserBlock
                        title="Usuário removido"
                        user={c.deleteduser}
                        chips={(
                          <>
                            <Chip size="small" label="excluído" color="error" variant="outlined" />
                            <Chip size="small" label="removido pela unificação" color="warning" variant="outlined" />
                          </>
                        )}
                      />
                      <UserBlock
                        title="Usuário principal"
                        user={c.principaluser}
                        chips={c.unifiedat ? <Chip size="small" label={`unificado em ${moment(c.unifiedat).format('DD/MM/YYYY HH:mm')}`} variant="outlined" /> : null}
                      />
                    </Box>

                    <Divider />

                    <Box>
                      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>O que será feito</Typography>
                      <Typography component="ul" sx={{ m: 0, pl: 3, fontSize: 14 }}>
                        <li>O e-mail do usuário principal passa de <strong>{c.principaluser?.email || '—'}</strong> para <strong>{c.deleteduser.email}</strong>.</li>
                        <li>
                          {c.activeoptins > 0
                            ? `${c.activeoptins} registro(s) de opt-in do principal serão marcados como excluídos, forçando novo fluxo de autenticação.`
                            : 'O principal não possui opt-in ativo; nada a invalidar.'}
                        </li>
                        <li>A alteração manual é registrada no histórico de unificação.</li>
                      </Typography>
                    </Box>

                    {c.blockers.length > 0 && (
                      <Alert severity="error">
                        {c.blockers.map((b) => <div key={b}>{b}</div>)}
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button variant="contained" color="warning" disabled={!c.canrevert} onClick={() => setConfirmCase(c)}>
                        Reverter unificação
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </Box>

        <Dialog open={!!confirmCase} onClose={() => !reverting && setConfirmCase(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirmar reversão</DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              O e-mail de login do usuário <strong>{confirmCase?.principaluser?.id}</strong> será trocado de{' '}
              <strong>{confirmCase?.principaluser?.email}</strong> para <strong>{confirmCase?.deleteduser?.email}</strong>.
              Esta ação altera o acesso do usuário e fica registrada em seu nome.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmCase(null)} disabled={reverting}>Cancelar</Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleRevert}
              disabled={reverting}
              startIcon={reverting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Reverter
            </Button>
          </DialogActions>
        </Dialog>

        <CustomizedSnackbars
          variant={snack.variant}
          message={snack.message}
          isOpen={snack.open}
          toClose={() => setSnack((s) => ({ ...s, open: false }))}
        />
      </main>
    </div>
  );
}

GovUnificationRevert.propTypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  actionsHeaderMenu: state.actionsHeaderMenu,
  headerMenu: state.headerMenu,
});

export default connect(mapStateToProps)(withStyles(Styles)(GovUnificationRevert));
